import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const TIMELINE = [
  { year: "2009–2012", title: "B.Com (Hons), Zakir Hussain College, University of Delhi", body: "Graduated with honors — the foundation of a discipline-led approach to numbers." },
  { year: "2013", title: "Chartered Accountant (CA)", body: "Earned the prestigious CA professional degree across accounting, audit, taxation and financial advisory." },
  { year: "2014", title: "Founded Stealodeal", body: "Started Stealodeal with a vision to be India’s most customer-centric brand. Built a national customer base from scratch." },
  { year: "Today", title: "Partner — Sharma Nitin & Co.", body: "Leads a dedicated team delivering accounting, audits, tax solutions, internal audits and risk advisory to a diverse client base across India." },
  { year: "Today", title: "Finance Creator", body: "Translates a decade of practice into content for students, professionals and founders on YouTube and Instagram." },
];

const SKILLS = [
  "Tax", "Income Tax", "Tax Preparation", "Accounting", "Accounting Education",
  "Statutory Audits", "Bank Audits", "Internal Audits", "Due Diligence",
  "Auditing", "Business Development", "International Business Development",
  "Startup Leadership", "Articulation", "Analytical Skills", "Business Ownership",
];

export default function About() {
  return (
    <>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 md:pt-24 pb-16 md:pb-24">
        <p className="overline fade-up"><span className="section-mark">About</span></p>
        <h1 className="font-display mt-7 text-[44px] sm:text-6xl md:text-7xl font-light tracking-[-0.03em] leading-[0.98] text-ink fade-up fade-up-2">
          A Chartered Accountant who<br />happens to love <span className="font-display-italic text-terracotta">storytelling</span>.
        </h1>
        <p className="mt-8 max-w-3xl text-[15px] sm:text-lg md:text-xl text-ink-soft leading-[1.7] fade-up fade-up-3">
          Passionate entrepreneur and seasoned Chartered Accountant with a strong academic
          foundation and a proven track record. Driven by a vision to deliver excellence in
          financial services — and by a deep belief that good finance, well explained, is a
          superpower for every business owner.
        </p>
      </section>

      {/* TWO COLUMN INTRO */}
      <section className="border-t border-[hsl(var(--line))]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="overline"><span className="section-mark">The short version</span></p>
            <h2 className="font-display mt-6 text-3xl sm:text-4xl font-light tracking-[-0.02em] text-ink leading-[1.15]">
              Trained at one of Delhi's most respected colleges.<br /><span className="font-display-italic">Tested across audits, tax, and entrepreneurship</span>.
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-ink-soft text-[15px] md:text-lg leading-[1.75]">
            <p>
              Graduated with honors from <strong className="text-ink font-semibold">Zakir Hussain College, University of Delhi</strong> with a
              Bachelor of Commerce, then earned the prestigious Chartered Accountant
              professional degree — solidifying expertise in accounting, auditing, taxation
              and financial advisory.
            </p>
            <p>
              In 2014, took the leap to start <strong className="text-ink font-semibold">Stealodeal</strong> — driven by a passion to become the
              most customer-centric brand. What began as a small venture has grown into a
              dynamic and trusted company, delivering products to a diverse client base.
            </p>
            <p>
              As Partner at <strong className="text-ink font-semibold">Sharma Nitin &amp; Company</strong>, leads a dedicated team in providing
              comprehensive accounting, auditing, tax solutions, internal audits and risk
              advisory services — committed to exceeding client expectations through
              personalized solutions and a relentless pursuit of excellence.
            </p>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="bg-white border-t border-[hsl(var(--line))]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32">
          <p className="overline"><span className="section-mark">Journey</span></p>
          <h2 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.03em] text-ink leading-[1.05]">
            A decade of work,<br />and a decade <span className="font-display-italic">still to come</span>.
          </h2>

          <ol className="mt-16 relative border-l border-[hsl(var(--line))] ml-2">
            {TIMELINE.map((t, i) => (
              <li
                key={i}
                data-testid={`timeline-${i}`}
                className="relative pl-8 md:pl-12 pb-12 last:pb-0"
              >
                <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-terracotta ring-4 ring-[hsl(var(--bg))]" />
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-soft">{t.year}</p>
                <h3 className="font-display mt-3 text-2xl md:text-3xl font-light tracking-[-0.02em] text-ink">
                  {t.title}
                </h3>
                <p className="mt-3 max-w-2xl text-ink-soft text-[15px] md:text-base leading-[1.75]">{t.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SKILLS */}
      <section className="border-t border-[hsl(var(--line))]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="overline"><span className="section-mark">Core skills</span></p>
              <h2 className="font-display mt-6 text-4xl sm:text-5xl font-light tracking-[-0.03em] text-ink leading-[1.05]">
                What sits inside<br /><span className="font-display-italic">the toolbox</span>.
              </h2>
              <p className="mt-6 text-ink-soft text-[15px] md:text-base leading-[1.7]">
                A decade-deep stack of practice. Equally at home with a balance sheet and a
                content calendar.
              </p>
            </div>
            <ul className="md:col-span-8 flex flex-wrap gap-2.5">
              {SKILLS.map((s, i) => (
                <li
                  key={s}
                  data-testid={`skill-${i}`}
                  className="rounded-full border border-stone-300 px-5 py-2.5 text-[13px] font-medium tracking-wide text-ink bg-white hover:border-ink hover:text-terracotta transition-colors"
                >
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-t border-[hsl(var(--line))] bg-ink text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.03em] leading-[1.02]">
            Want to work <span className="font-display-italic">together</span>?
          </h2>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="inline-flex items-center rounded-full bg-terracotta hover:bg-terracotta-deep px-7 py-4 text-[14px] font-medium tracking-wide transition-colors"
          >
            Start a conversation <ArrowUpRight className="ml-2" size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
